import {
  BotType,
  ProcessTimeBucket,
  type CarrierSuccessRateDto,
  type ProcessTimeBucket as ProcessTimeBucketType,
} from '../../../api/dto/get-payment-bot-dashboard.dto'
import {
  GREEN,
  GOLD,
  RED,
  ERROR_CODE_LABELS,
  PROCESS_TIME_BUCKET_LABELS,
  UNKNOWN_ERROR_CODE,
} from './constants'
import type { DashboardSummary } from './types'

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

export function formatErrorCode(code: string | null | undefined): string {
  if (!code) return 'Unknown Error'
  const trimmed = code.trim()
  if (ERROR_CODE_LABELS[trimmed]) return ERROR_CODE_LABELS[trimmed]

  return trimmed
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function toErrorCodeFilter(code: string | null | undefined): string {
  return code || UNKNOWN_ERROR_CODE
}

export function parseProcessTimeBucket(
  value: unknown
): ProcessTimeBucketType | null {
  return Object.values(ProcessTimeBucket).find((v) => v === value) ?? null
}

export function getProcessTimeBucketLabel(
  bucket: ProcessTimeBucketType
): string {
  return PROCESS_TIME_BUCKET_LABELS[bucket]
}

// PPS error messages are shaped `"<Category> — <detail>"` (see
// partner-payment-service pps-error.ts). The category prefix is already shown in
// the Error Code column, so the Note column strips it to surface just the detail.
// Splits on the FIRST ` — ` only, so a detail that contains its own ` — `
// (e.g. carrierRejected: "… by these inputs — memberId: …") is preserved intact.
const CATEGORY_SEPARATOR = ' — '

export function stripCategoryPrefix(note: string | null): string | null {
  if (!note) return note
  const idx = note.indexOf(CATEGORY_SEPARATOR)
  return idx === -1 ? note : note.slice(idx + CATEGORY_SEPARATOR.length)
}

// Humanize a raw second count into the largest sensible unit so the dashboard
// stops showing values like "519600.8s" (~6 days). Sub-minute keeps one decimal
// for precision; minute/hour/day tiers drop to whole units with a single
// remainder so the cell stays scannable (e.g. "2h 31m", "6d 4h").
export function formatDuration(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec <= 0) return '0s'
  if (totalSec < 60) return `${round1(totalSec)}s`
  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60)
    const s = Math.round(totalSec % 60)
    return s ? `${m}m ${s}s` : `${m}m`
  }
  if (totalSec < 86400) {
    const h = Math.floor(totalSec / 3600)
    const m = Math.round((totalSec % 3600) / 60)
    return m ? `${h}h ${m}m` : `${h}h`
  }
  const d = Math.floor(totalSec / 86400)
  const h = Math.round((totalSec % 86400) / 3600)
  return h ? `${d}d ${h}h` : `${d}d`
}

export function rateColor(rate: number): string {
  if (rate >= 90) return GREEN
  if (rate >= 70) return GOLD
  return RED
}

// Health palette — same hexes as rateColor().
const PROCESS_TIME_GREEN = GREEN
const PROCESS_TIME_GOLD = GOLD
const PROCESS_TIME_RED = RED

// Per-bot-type process-time health bands, in SECONDS. Lower is better, and the
// "normal" range differs by bot type: IVR emits BOT_PROCESSING so its event-time
// is real run time (~1–2 min); WEB_AGENT does not, so its event-time falls back
// to BOT_QUEUED and includes queue wait — held to a tighter run-focused bar.
// `sec <= green` → green, `sec <= yellow` → gold, else red (EMS-8614).
const PROCESS_TIME_BANDS: Record<string, { green: number; yellow: number }> = {
  [BotType.IVR]: { green: 60, yellow: 120 },
  [BotType.WEB_AGENT]: { green: 30, yellow: 120 },
}

// Health color for a process-time value, keyed by bot type. Returns undefined
// (→ no color, plain text) for a null time or an unmapped bot type (e.g. API),
// so an unknown type is never mislabeled with someone else's standard.
export function processTimeColor(
  sec: number | null,
  botType: BotType | null
): string | undefined {
  if (sec == null) return undefined
  const band = botType ? PROCESS_TIME_BANDS[botType] : undefined
  if (!band) return undefined
  if (sec <= band.green) return PROCESS_TIME_GREEN
  if (sec <= band.yellow) return PROCESS_TIME_GOLD
  return PROCESS_TIME_RED
}

export function computeDashboardSummary(
  rows: CarrierSuccessRateDto[],
  overallMedianProcessingTimeSec: number
): DashboardSummary {
  const totalBots = rows.length
  const webAgentBots = rows.filter(
    (row) => row.botType === BotType.WEB_AGENT
  ).length
  const ivrBots = rows.filter((row) => row.botType === BotType.IVR).length

  const totalBenefits = rows.reduce((sum, row) => sum + row.totalBenefits, 0)
  const succeededBenefits = rows.reduce(
    (sum, row) => sum + row.completedCount,
    0
  )
  const failedBenefits = rows.reduce((sum, row) => sum + row.failedCount, 0)
  const pendingBenefits = rows.reduce((sum, row) => sum + row.pendingCount, 0)

  const overallSuccessRate =
    totalBenefits === 0 ? 0 : round1((succeededBenefits / totalBenefits) * 100)

  // Weight by completed+failed (= distinct_benefits with terminal tasks) so the
  // fleet rate matches the denominator each carrier rate was computed with.
  // Using totalBenefits (incl. pending) would dilute the weight incorrectly.
  const totalWithTasks = succeededBenefits + failedBenefits
  const weightedFirst = rows.reduce(
    (sum, row) =>
      sum + row.firstAttemptRate * (row.completedCount + row.failedCount),
    0
  )
  const weightedEventual = rows.reduce(
    (sum, row) =>
      sum + row.eventualRate * (row.completedCount + row.failedCount),
    0
  )
  const overallFirstAttemptRate =
    totalWithTasks === 0 ? 0 : round1(weightedFirst / totalWithTasks)
  const overallEventualRate =
    totalWithTasks === 0 ? 0 : round1(weightedEventual / totalWithTasks)

  // Use the server-side pooled-event median (same statistic as the all-carrier
  // detail KPI) so both views show an identical number. A frontend mean of
  // per-carrier medians is a different, volume-blind statistic and diverged
  // sharply (~11m vs ~4m) on right-skewed carrier distributions.
  const medianProcessingTimeSec = overallMedianProcessingTimeSec

  return {
    totalBots,
    webAgentBots,
    ivrBots,
    totalBenefits,
    succeededBenefits,
    failedBenefits,
    pendingBenefits,
    overallSuccessRate,
    overallFirstAttemptRate,
    overallEventualRate,
    medianProcessingTimeSec,
  }
}
